import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { fmtMoeda } from '@/lib/utils/formatters';
import {
  STATUS_PRODUTO_LABEL,
  type SegregacaoIgn,
  type StatusProduto,
} from '@/services/SimplesNacional/Segregacao/api/types';

const PAGE_SIZE = 5;

interface ProdutosIgnoradosTableProps {
  data: SegregacaoIgn[];
}

export function ProdutosIgnoradosTable({ data }: ProdutosIgnoradosTableProps) {
  const [busca, setBusca] = useState('');
  const [limite, setLimite] = useState(PAGE_SIZE);

  const filtrados = busca
    ? data.filter(
        item =>
          item.Produto?.toLowerCase().includes(busca.toLowerCase()) ||
          item.Descricao?.toLowerCase().includes(busca.toLowerCase()) ||
          item.NCM?.includes(busca)
      )
    : data;

  const visiveis = busca ? filtrados : filtrados.slice(0, limite);
  const restantes = busca ? 0 : filtrados.length - limite;

  if (data.length === 0) {
    return (
      <p className="px-4 py-6 text-sm text-muted-foreground text-center">
        Nenhum produto ignorado encontrado.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="px-4 pt-2">
        <Input
          placeholder="Buscar por produto, descrição ou NCM..."
          value={busca}
          onChange={e => {
            setBusca(e.target.value);
            setLimite(PAGE_SIZE);
          }}
          className="max-w-sm h-8 text-xs"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Produto</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Descrição</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">NCM</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">CFOP</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Status</th>
              <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">
                Valor Total
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Data</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((item, i) => {
              const statusInfo = STATUS_PRODUTO_LABEL[item.Status as StatusProduto];
              return (
                <tr key={i} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-2 max-w-[140px] truncate" title={item.Produto}>
                    {item.Produto || '—'}
                  </td>
                  <td className="px-4 py-2 max-w-[200px] truncate" title={item.Descricao}>
                    {item.Descricao || '—'}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{item.NCM || '—'}</td>
                  <td className="px-4 py-2 text-muted-foreground">{item.CFOP || '—'}</td>
                  <td className="px-4 py-2">
                    {statusInfo ? (
                      <Badge className={`${statusInfo.color} text-white text-[10px] px-1.5 py-0`}>
                        {statusInfo.label}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">{item.Status || '—'}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">
                    {fmtMoeda(item.ValTot)}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                    {item.DataInclusao || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-4 pb-2 flex items-center justify-between text-xs text-muted-foreground border-t pt-2">
        <span>
          {visiveis.length} de {filtrados.length} produto(s)
        </span>
        {restantes > 0 && (
          <button
            onClick={() => setLimite(l => l + PAGE_SIZE)}
            className="text-primary hover:underline font-medium"
          >
            Ver mais ({restantes} restante{restantes !== 1 ? 's' : ''})
          </button>
        )}
      </div>
    </div>
  );
}
